<template>
  <div>
    <b-card
      no-body
      class="notification-center mb-0"
    >
      <div class="notification-toolbar">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            lg="7"
            class="mb-1 mb-lg-0"
          >
            <div class="d-flex align-items-center flex-wrap">
              <b-button
                class="mr-50 mb-50"
                :variant="!showArchived ? 'primary' : 'outline-primary'"
                @click="setArchived(false)"
              >
                <feather-icon icon="InboxIcon" />
                <span class="align-middle ml-50">Inbox</span>
              </b-button>
              <b-button
                class="mr-1 mb-50"
                :variant="showArchived ? 'primary' : 'outline-primary'"
                @click="setArchived(true)"
              >
                <feather-icon icon="ArchiveIcon" />
                <span class="align-middle ml-50">Archive</span>
              </b-button>

              <label class="mb-50">Show</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50 mb-50"
              />
              <label class="mb-50">entries</label>
            </div>
          </b-col>

          <b-col
            cols="12"
            lg="5"
          >
            <div class="d-flex justify-content-lg-end flex-wrap">
              <b-button
                v-if="!showArchived"
                variant="outline-primary"
                class="mr-1 mb-50"
                @click="onMarkAllRead"
              >
                <feather-icon icon="CheckCircleIcon" />
                <span class="align-middle ml-50">Mark all read</span>
              </b-button>
              <b-button
                v-if="!showArchived"
                variant="outline-secondary"
                class="mb-50"
                @click="onMarkAllUnread"
              >
                <feather-icon icon="CircleIcon" />
                <span class="align-middle ml-50">Mark all unread</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <div class="notification-list">
        <div
          v-if="loading"
          class="notification-loading"
        >
          <b-spinner
            variant="primary"
            small
            class="mr-50"
          />
          Loading notifications...
        </div>

        <div
          v-else-if="!pageNotifications.length"
          class="notification-empty"
        >
          <feather-icon
            :icon="showArchived ? 'ArchiveIcon' : 'BellOffIcon'"
            size="36"
            class="mb-1"
          />
          <h5 class="mb-25">
            {{ showArchived ? 'Archive is empty' : 'No notifications yet' }}
          </h5>
          <p class="mb-0 text-muted">
            {{ showArchived ? 'Archived notifications will appear here.' : 'New activity will appear here as it happens.' }}
          </p>
        </div>

        <div
          v-for="notification in pageNotifications"
          :key="notification.notification_id"
          class="notification-row"
          :class="{ 'is-unread': !notification.notification_is_read }"
        >
          <button
            type="button"
            class="notification-main"
            @click="openNotification(notification)"
          >
            <span
              class="unread-dot"
              :class="{ invisible: notification.notification_is_read }"
            />
            <b-avatar
              size="42"
              :variant="typeVariant(notification.notification_type)"
              class="mr-1 flex-shrink-0"
            >
              <feather-icon :icon="typeIcon(notification.notification_type)" />
            </b-avatar>
            <span class="notification-content">
              <span class="notification-title">
                {{ notification.notification_title || '-' }}
              </span>
              <span class="notification-body">
                {{ truncateText(notification.notification_body) }}
              </span>
              <span class="notification-meta">
                <b-badge
                  :variant="typeVariant(notification.notification_type)"
                  class="mr-50"
                >
                  {{ notification.notification_type || 'info' }}
                </b-badge>
                <span>{{ titleizeModule(notification.notification_module) }}</span>
                <span class="mx-50">/</span>
                <span>{{ formatTime(notification.notification_created_at) }}</span>
              </span>
            </span>
          </button>

          <div class="notification-actions">
            <b-button
              v-if="!notification.notification_is_read"
              v-b-tooltip.hover
              title="Mark as read"
              variant="flat-primary"
              size="sm"
              @click="onMarkRead(notification)"
            >
              <feather-icon icon="CheckIcon" />
            </b-button>
            <b-button
              v-else
              v-b-tooltip.hover
              title="Mark as unread"
              variant="flat-secondary"
              size="sm"
              @click="onMarkUnread(notification)"
            >
              <feather-icon icon="CircleIcon" />
            </b-button>
            <b-button
              v-if="!showArchived"
              v-b-tooltip.hover
              title="Archive"
              variant="flat-secondary"
              size="sm"
              @click="onArchive(notification)"
            >
              <feather-icon icon="ArchiveIcon" />
            </b-button>
            <b-button
              v-else
              v-b-tooltip.hover
              title="Move to inbox"
              variant="flat-secondary"
              size="sm"
              @click="onUnarchive(notification)"
            >
              <feather-icon icon="InboxIcon" />
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Delete"
              variant="flat-danger"
              size="sm"
              @click="onDelete(notification)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </div>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalNotifications || (currentPage * perPage)"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment-timezone'
import useNotifications from '@/composables/useNotifications'
import listPagePersistence from '@/mixins/listPagePersistence'

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BPagination,
    BRow,
    BSpinner,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [listPagePersistence(['currentPage'])],
  setup() {
    const {
      fetchNotifications,
      fetchUnreadCount,
      markRead,
      markUnread,
      markAllRead,
      markAllUnread,
      archive,
      unarchive,
      remove,
    } = useNotifications()

    return {
      fetchNotifications,
      fetchUnreadCount,
      markRead,
      markUnread,
      markAllRead,
      markAllUnread,
      archive,
      unarchive,
      remove,
    }
  },
  data() {
    return {
      loading: false,
      pageNotifications: [],
      perPage: 10,
      currentPage: 1,
      totalNotifications: 0,
      showArchived: false,
      perPageOptions: [10, 25, 50, 100],
    }
  },
  computed: {
    dataMeta() {
      const localItemsCount = this.pageNotifications.length
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalNotifications,
      }
    },
  },
  watch: {
    perPage() {
      this.currentPage = 1
      this.loadNotifications()
    },
    currentPage() {
      this.loadNotifications()
    },
  },
  mounted() {
    this.loadNotifications()
  },
  methods: {
    async loadNotifications() {
      this.loading = true
      try {
        const response = await this.fetchNotifications({
          limit: this.perPage,
          offset: this.perPage * (this.currentPage - 1),
          archived: this.showArchived ? 'true' : 'false',
        }, { updateStore: false })

        this.totalNotifications = response.data_count || 0
        this.pageNotifications = response.data || []
      } catch (e) {
        this.$toast.error('Failed to load notifications')
        this.pageNotifications = []
      } finally {
        this.loading = false
      }
    },
    setArchived(value) {
      if (this.showArchived === value) return
      this.showArchived = value
      this.currentPage = 1
      this.loadNotifications()
    },
    typeVariant(type) {
      return {
        success: 'light-success',
        warning: 'light-warning',
        urgent: 'light-danger',
      }[type] || 'light-info'
    },
    typeIcon(type) {
      return {
        success: 'CheckCircleIcon',
        warning: 'AlertTriangleIcon',
        urgent: 'AlertOctagonIcon',
      }[type] || 'BellIcon'
    },
    titleizeModule(value) {
      if (!value) return '-'
      return value
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatTime(value) {
      if (!value) return '-'
      return moment(value).format('DD MMM YYYY HH:mm')
    },
    truncateText(value) {
      if (!value) return '-'
      const text = String(value)
      return text.length > 150 ? `${text.slice(0, 150)}...` : text
    },
    async openNotification(notification) {
      if (!notification.notification_is_read) {
        await this.onMarkRead(notification, false)
      }
      if (notification.notification_link) {
        this.$router.push(notification.notification_link)
      }
    },
    async onMarkRead(notification, shouldRefresh = true) {
      await this.markRead(notification.notification_id)
      await this.fetchUnreadCount()
      notification.notification_is_read = true
      if (shouldRefresh) this.loadNotifications()
    },
    async onMarkUnread(notification) {
      await this.markUnread(notification.notification_id)
      await this.fetchUnreadCount()
      notification.notification_is_read = false
      this.loadNotifications()
    },
    async onMarkAllRead() {
      await this.markAllRead()
      await this.fetchUnreadCount()
      this.loadNotifications()
    },
    async onMarkAllUnread() {
      await this.markAllUnread()
      await this.fetchUnreadCount()
      this.loadNotifications()
    },
    async onArchive(notification) {
      await this.archive(notification.notification_id)
      await this.fetchUnreadCount()
      this.loadNotifications()
    },
    async onUnarchive(notification) {
      await this.unarchive(notification.notification_id)
      await this.fetchUnreadCount()
      this.loadNotifications()
    },
    async onDelete(notification) {
      await this.remove(notification.notification_id)
      await this.fetchUnreadCount()
      this.loadNotifications()
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-center {
  overflow-x: hidden;
}
.notification-toolbar,
.notification-list,
.notification-row,
.notification-main,
.notification-content {
  max-width: 100%;
}
.notification-content {
  overflow: hidden;
}
.notification-title,
.notification-body,
.notification-meta {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.per-page-selector {
  width: 90px;
}
.notification-toolbar {
  border-bottom: 1px solid #ebe9f1;
  padding: 1.25rem;
}
.notification-list {
  min-height: 320px;
}
.notification-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #ebe9f1;
  background-color: #fff;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.notification-row:hover {
  background-color: #f8f8fb;
}
.notification-row.is-unread {
  background-color: #f7f6ff;
}
.notification-main {
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 1.1rem 1.25rem;
  text-align: left;
  color: inherit;
}
.notification-main:focus {
  outline: none;
}
.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #7367f0;
  margin: 17px 0.9rem 0 0;
  flex-shrink: 0;
}
.invisible {
  visibility: hidden;
}
.notification-content {
  display: block;
  min-width: 0;
}
.notification-title {
  display: block;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.35;
}
.notification-body {
  display: block;
  margin-top: 0.25rem;
  color: #6e6b7b;
  line-height: 1.45;
}
.notification-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0.6rem;
  color: #82868b;
  font-size: 0.82rem;
}
.notification-actions {
  display: flex;
  align-items: center;
  padding: 0 1rem;
  flex-shrink: 0;
}
.notification-loading,
.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  color: #82868b;
  text-align: center;
}
@media (max-width: 767.98px) {
  .notification-row {
    display: block;
  }
  .notification-main {
    padding-bottom: 0.5rem;
  }
  .notification-actions {
    justify-content: flex-end;
    padding: 0 1rem 1rem;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
